/**
 * Single-pass guest stats sampler.
 *
 * One `getStats()` report is turned into three shapes at once:
 *   - `raw`        — the telemetry shape the existing METRICS_WEBRTC_QUALITY pipeline expects,
 *   - `congestion` — the normalized input to the shared congestion ladder,
 *   - `wire`       — the `clientStats` payload sent to the host.
 *
 * One pass, one set of baselines. The guest is a mobile device on a radio; polling `getStats()`
 * separately for telemetry and for congestion would double the cost of the most expensive
 * observation the client makes. Reading the report is delegated to `readInboundRtp`; this
 * file owns the differencing, the windows and the three output shapes.
 *
 * Counters are differenced against the previous sample rather than read cumulatively: a
 * lifetime loss counter averages a spike away over a long session, which is exactly when it
 * matters most.
 *
 * PLATFORM NOTE — `freezeCount`, `totalInterFrameDelay`, `framesReceived` and the jitter-buffer
 * counters are Chromium-class stats Safari omits. The Phase 1 capability matrix
 * (guestBrowserCapability.ts) is the authority over which stats are real per engine: when a
 * capability is supplied, a field the engine cannot measure is omitted from the `congestion`
 * and `wire` shapes regardless of what the report carries, so an emulator or instrumentation
 * report cannot fabricate a healthy reading. The ladder reads absence as absence; standing
 * queue, loss rate and playout-latency growth come from stats every engine emits.
 *
 * JITTER BUFFER — the interval jitter-buffer delay is the REAL inbound-rtp counter on every
 * engine that exposes it, Android included. An earlier revision replaced it on Android with
 * `standingBacklogFrames / fps`; that synthetic oscillated with the reported framerate, read
 * a standing decoder depth as "growth", and hid the ~320 ms buffer the counters were showing.
 * Android's pinned jitter buffer only changes the warm-up: the playout-latency tracker holds
 * its reading at null until the pipeline's steady depth is in the baseline.
 *
 * The `raw` telemetry shape is NOT covered by the capability matrix: CoreTypes types
 * `recentFreezeDelta` and `avgInterFrameDelay` as plain numbers, so those two
 * `recommendationFromStats` triggers stay unreachable on Safari (out of scope here).
 */

import type {
  PeerQualityRecommendation,
  PlaycastWebrtcQualityRawStats,
} from '@playcastdotio/CoreTypes';
import { RttFloorTracker } from './rttFloorTracker';
import { PLAYOUT_LATENCY_PINNED_WARMUP_MS, PlayoutLatencyTracker } from './playoutLatencyTracker';
import type { GuestBrowserCapability } from './guestBrowserCapability';
import type { StreamCongestionInputs } from './streamCongestionSignal';
import type { ClientStreamStatsPayload } from './clientStreamStatsContracts';
import { isDebugWebrtcLoggingEnabled } from './debugLoggingFlag';
import { readInboundRtp, type InboundRtpReading, type DecoderStatsObservation } from './guestStreamStatsReader';
import type { SelectedNetworkPath } from './streamRecovery/contracts';

/** The guest tracks exactly one link (to the host), so both trackers need only one key. */
const HOST_LINK_KEY = 'host';
/** Framerate samples retained for the stability / min / max window. */
const FRAMERATE_WINDOW_SAMPLES = 10;
/** Below this the stream is not delivering a usable picture. */
const MIN_ACCEPTABLE_FPS = 20;
/** Framerate at or below this with an active video track is a panic. */
const FRAMERATE_PANIC_FPS = 10;
/** Video bitrate at or below this with an active video track is a panic. */
const BITRATE_PANIC_KBPS = 100;
/** Spread across the framerate window above which the stream is judged unstable. */
const FRAMERATE_STABILITY_SPREAD = 15;
/** Decode time per frame above which the decoder is behind, ms. */
const DECODER_OVERRUN_MS = 20;
/** Loss rate above which quality should step down, percent. */
const LOSS_DECREASE_PCT = 2;
/** Freezes in one interval above which quality should step down. */
const FREEZE_DECREASE_DELTA = 1;
/** Inter-frame delay above which quality should step down, ms. */
const INTER_FRAME_DELAY_DECREASE_MS = 60;
/** NACKs in one interval above which quality should step down. */
const NACK_DECREASE_DELTA = 30;

export type GuestStatsSample = {
  decodeSample?: DecoderStatsObservation;
  selectedPath?: SelectedNetworkPath;
  /** Telemetry shape; satisfies the Messaging metrics rawStatsSchema. */
  raw: PlaycastWebrtcQualityRawStats;
  /** Congestion shape; feeds evaluateCongestionSignal. */
  congestion: StreamCongestionInputs;
  /** Wire shape; sent to the host as a clientStats message. */
  wire: ClientStreamStatsPayload;
  /** framesDecoded observed this poll; 0 when unavailable. */
  framesDecoded: number;
  /**
   * The decoder's standing depth this poll: frames received but neither decoded nor dropped,
   * ABSOLUTE (not an interval delta). What a player waits behind. Undefined when the engine
   * does not expose framesReceived (Safari).
   */
  standingBacklogFrames?: number;
  /**
   * True when the platform supplied the Chromium-only freeze / inter-frame-delay stats. False
   * on Safari, where both are absent — so a consumer can tell "no freezes" from "freezes are
   * not measurable here". When false, `congestion.freezeDelta` and `wire.freezeDelta` are
   * omitted rather than reported as 0: a hard 0 is a claim about the link, absence a claim
   * about the browser, and the ladder must be able to tell them apart.
   */
  freezeSignalAvailable: boolean;
};

export type GuestStreamStatsSamplerOptions = {
  nowMs?: () => number;
  isMobile?: boolean;
  /**
   * The engine's measurement capabilities (Phase 1 matrix): the authority over which freeze /
   * jitter-buffer / frames-received stats are real. When omitted, the sampler trusts the
   * report shape, which keeps the existing behavior.
   */
  capability?: GuestBrowserCapability;
};

type CounterBaseline = {
  packetsLost: number;
  packetsReceived: number;
  bytesReceived: number;
  bytesReceivedAvailable: boolean;
  freezeCount: number;
  freezeCountAvailable: boolean;
  totalFreezesDuration?: number;
  nackCount: number;
  framesDecoded: number;
  framesReceived: number;
  framesReceivedAvailable: boolean;
  framesDropped: number;
  /** Cumulative seconds spent inside the decoder, for the interval decode-time average. */
  totalDecodeTime: number;
  audioBytesReceived: number;
  audioPacketsLost: number;
  audioPacketsReceived: number;
  atMs: number;
};

/** Cumulative jitter-buffer sums from the previous poll; NaN where the engine omits one. */
type JitterBufferCounters = { delay: number; target: number; minimum: number; emitted: number };

/** Interval-average jitter-buffer figures, ms; null until two polls with emitted frames exist. */
type JitterBufferInterval = { delayMs: number | null; targetMs: number | null; minimumMs: number | null };
const NO_JITTER_BUFFER: JitterBufferInterval = { delayMs: null, targetMs: null, minimumMs: null };

/** Previous-poll audio (NetEq) jitter-buffer sums; NaN where the engine omits the target. */
type AudioJitterBufferCounters = { delay: number; target: number; emitted: number };
type AudioJitterBufferInterval = { delayMs: number | null; targetMs: number | null };
const NO_AUDIO_JITTER_BUFFER: AudioJitterBufferInterval = { delayMs: null, targetMs: null };

export class GuestStreamStatsSampler {
  private readonly nowMs: () => number;
  private readonly isMobile: boolean;
  private readonly capability?: GuestBrowserCapability;
  private readonly rttFloor: RttFloorTracker;
  private readonly playoutLatency: PlayoutLatencyTracker;
  private baseline: CounterBaseline | null = null;
  private prevJitterBuffer: JitterBufferCounters | null = null;
  private prevAudioJitterBuffer: AudioJitterBufferCounters | null = null;
  private framerateWindow: number[] = [];
  private recentFreezes: { timestamp: number; count: number }[] = [];
  private recentNacks: { timestamp: number; count: number }[] = [];
  private packetSamples: number[] = [];

  constructor(options: GuestStreamStatsSamplerOptions = {}) {
    this.nowMs = options.nowMs ?? (() => Date.now());
    this.isMobile = options.isMobile ?? false;
    this.capability = options.capability;
    this.rttFloor = new RttFloorTracker({ nowMs: this.nowMs });
    // Android pins jitterBufferTarget to 0 and a hardware decoder keeps a fixed number of
    // frames in flight, so the first seconds of a session read as "growth" over a still-
    // filling pipeline — which is how a clean guest got stepped down to 360p/30fps five
    // seconds in. The tracker is NOT seeded at 0 and holds null through its warm-up.
    this.playoutLatency = new PlayoutLatencyTracker({
      nowMs: this.nowMs,
      warmupMs: this.capability?.jitterBufferPinned ? PLAYOUT_LATENCY_PINNED_WARMUP_MS : 0,
    });
  }

  /** Drop every accumulated baseline and window. Call on a new connection. */
  public reset(): void {
    this.prevJitterBuffer = null;
    this.prevAudioJitterBuffer = null;
    this.baseline = null;
    this.framerateWindow = [];
    this.recentFreezes = [];
    this.recentNacks = [];
    this.packetSamples = [];
    this.rttFloor.clearAll();
    // A new connection restarts the pinned-path warm-up (a fresh decoder pipeline refills).
    this.playoutLatency.clearAll();
  }

  /** A new path invalidates differencing but preserves the device/capability configuration. */
  public resetForPath(): void { this.reset(); }

  /** Returns null when the report carries no inbound video stats. */
  public sample(report: RTCStatsReport): GuestStatsSample | null {
    const reading = readInboundRtp(report);
    if (!reading.hasVideo) {
      // DEBUG BUILD ONLY — a poll that produces no sample is INVISIBLE on the wire (the
      // Android "nothing ever fires" shape), so the debug build says so instead of returning
      // a silent null. Live check so a test or a page can flip the debug flag at runtime.
      if (isDebugWebrtcLoggingEnabled()) {
        console.log('[GuestStats.no-video]', {
          rttMs: reading.rttMs ?? 'absent',
          candidatesSampled: reading.rttMs !== undefined,
          framesPerSecond: reading.framesPerSecond ?? 'absent',
        });
      }
      return null;
    }

    const now = this.nowMs();
    const previous = this.baseline;
    this.baseline = {
      packetsLost: reading.packetsLost ?? previous?.packetsLost ?? 0,
      packetsReceived: reading.packetsReceived ?? previous?.packetsReceived ?? 0,
      bytesReceived: reading.bytesReceived ?? previous?.bytesReceived ?? 0,
      bytesReceivedAvailable: reading.bytesReceived !== undefined,
      freezeCount: reading.freezeCount ?? previous?.freezeCount ?? 0,
      freezeCountAvailable: reading.freezeCount !== undefined && reading.freezeCount >= 0,
      totalFreezesDuration: reading.totalFreezesDuration,
      nackCount: reading.nackCount ?? previous?.nackCount ?? 0,
      framesDecoded: reading.framesDecoded ?? previous?.framesDecoded ?? 0,
      framesReceived: reading.framesReceived ?? previous?.framesReceived ?? 0,
      framesReceivedAvailable: reading.framesReceived !== undefined,
      framesDropped: reading.framesDropped ?? previous?.framesDropped ?? 0,
      totalDecodeTime: reading.totalDecodeTime ?? previous?.totalDecodeTime ?? 0,
      audioBytesReceived: reading.audioBytesReceived ?? previous?.audioBytesReceived ?? 0,
      audioPacketsLost: reading.audioPacketsLost ?? previous?.audioPacketsLost ?? 0,
      audioPacketsReceived: reading.audioPacketsReceived ?? previous?.audioPacketsReceived ?? 0,
      atMs: now,
    };

    // Safari emits neither `freezeCount` nor `totalInterFrameDelay`, so on an iOS guest both
    // freeze-shaped triggers read a permanent zero — indistinguishable from a healthy link.
    // Track availability so absence can be reported as absence. An injected capability is
    // the authority; the report shape only decides when the caller gives no capability.
    const freezeSignalAvailable = reading.freezeCount !== undefined && reading.freezeCount >= 0;
    const framesReceivedSignalAvailable = this.capability?.exposesFramesReceived ?? reading.framesReceived !== undefined;

    const intervalMs = previous ? Math.max(0, now - previous.atMs) : 0;
    const lostDelta = delta(reading.packetsLost, previous?.packetsLost);
    const receivedDelta = delta(reading.packetsReceived, previous?.packetsReceived);
    const bytesDelta = delta(reading.bytesReceived, previous?.bytesReceived);
    const freezeDelta = delta(reading.freezeCount, previous?.freezeCount);
    const usableFreezeDelta = previous?.freezeCountAvailable && freezeSignalAvailable && intervalMs > 0 &&
      reading.freezeCount! >= previous.freezeCount ? freezeDelta : undefined;
    const freezeMs = previous?.totalFreezesDuration !== undefined && reading.totalFreezesDuration !== undefined &&
      intervalMs > 0 && reading.totalFreezesDuration >= previous.totalFreezesDuration
      ? (reading.totalFreezesDuration - previous.totalFreezesDuration) * 1000 : undefined;
    const nackDelta = delta(reading.nackCount, previous?.nackCount);
    const decodedDelta = delta(reading.framesDecoded, previous?.framesDecoded);
    const receivedFramesDelta = delta(reading.framesReceived, previous?.framesReceived);
    const droppedFramesDelta = delta(reading.framesDropped, previous?.framesDropped);

    const totalPackets = lostDelta + receivedDelta;
    const packetLossPercentage = totalPackets > 0 ? (lostDelta / totalPackets) * 100 : 0;

    const bitsPerSecond = intervalMs > 0 ? (bytesDelta * 8) / (intervalMs / 1000) : 0;
    const videoKbps = bitsPerSecond / 1000;
    const videoBitrateBps =
      previous?.bytesReceivedAvailable && intervalMs > 0 && reading.bytesReceived !== undefined
        ? bitsPerSecond
        : undefined;

    // Audio (observability-only): interval bitrate + loss rate, computed the same way as
    // video. Omitted from the wire until at least one prior baseline exists.
    const audioBytesDelta = delta(reading.audioBytesReceived, previous?.audioBytesReceived);
    const audioLostDelta = delta(reading.audioPacketsLost, previous?.audioPacketsLost);
    const audioReceivedDelta = delta(reading.audioPacketsReceived, previous?.audioPacketsReceived);
    const audioTotal = audioLostDelta + audioReceivedDelta;
    const audioBitrateBps =
      previous && intervalMs > 0 && reading.audioBytesReceived !== undefined ? (audioBytesDelta * 8) / (intervalMs / 1000) : undefined;
    const audioPacketsLostPct =
      previous && reading.audioPacketsLost !== undefined && audioTotal > 0 ? (audioLostDelta / audioTotal) * 100 : undefined;

    // Discarded frames are not waiting for decode. In zero-delay mode these drops are
    // intentional; treating them as queued frames causes unnecessary recovery cuts.
    const decodeBacklogFrames =
      reading.framesReceived !== undefined && reading.framesDecoded !== undefined ? Math.max(0, receivedFramesDelta - decodedDelta - droppedFramesDelta) : undefined;

    if (reading.framesPerSecond !== undefined) this.pushWindow(reading.framesPerSecond);
    if (freezeDelta > 0) this.recentFreezes.push({ timestamp: now, count: freezeDelta });
    if (nackDelta > 0) this.recentNacks.push({ timestamp: now, count: nackDelta });
    if (previous) this.packetSamples.push(lostDelta);
    this.recentFreezes = this.recentFreezes.slice(-FRAMERATE_WINDOW_SAMPLES);
    this.recentNacks = this.recentNacks.slice(-FRAMERATE_WINDOW_SAMPLES);
    this.packetSamples = this.packetSamples.slice(-FRAMERATE_WINDOW_SAMPLES);

    // A poll with no succeeded candidate pair must not record a 0 ms RTT: a zero-pinned floor
    // reads a healthy 30–99 ms link as a standing queue and manufactures false congestion.
    const standingQueueMs = reading.rttMs !== undefined ? this.rttFloor.standingQueueMs(HOST_LINK_KEY, reading.rttMs) : null;
    const rttFloorMs = reading.rttMs !== undefined && standingQueueMs !== null ? reading.rttMs - standingQueueMs : undefined;

    // The decoder's standing depth: frames delivered but neither emitted nor discarded.
    // ABSOLUTE, unlike `decodeBacklogFrames` above (this tick's growth). Frames dropped
    // before decode never reach framesDecoded, so they are subtracted or the depth would
    // grow for the life of the session. This is the number a player actually waits behind.
    const standingBacklogFrames =
      framesReceivedSignalAvailable && reading.framesReceived !== undefined && reading.framesDecoded !== undefined
        ? Math.max(0, reading.framesReceived - reading.framesDecoded - (reading.framesDropped ?? 0))
        : undefined;

    // Interval-average playout delay from cumulative counter deltas, on EVERY engine that
    // exposes the counters. The lifetime average (delay/emitted since connect) moves
    // glacially and buries a climb that started seconds ago. The trend is growth of this real
    // figure above its window minimum; on Android the tracker's warm-up (constructor) holds it
    // at null until the pipeline's steady depth is in the baseline. `standingBacklogFrames`
    // is never converted into a delay here — a standing depth is not latency growth.
    const jitterBuffer = this.intervalJitterBuffer(reading);
    const audioJitterBuffer = this.intervalAudioJitterBuffer(reading);
    const playoutLatencyTrendMs =
      jitterBuffer.delayMs !== null ? this.playoutLatency.observe(HOST_LINK_KEY, jitterBuffer.delayMs) : null;

    const averageDecodeTimePerFrameMs =
      decodedDelta > 0 && reading.totalDecodeTime !== undefined
        ? (reading.totalDecodeTime * 1000) / Math.max(1, reading.framesDecoded ?? 1)
        : 0;

    // Decoder residency this interval: wall time per frame inside the decoder, averaged over
    // the frames decoded since the last poll (the lifetime average above moves glacially).
    // The one decode-side depth signal every engine exposes — Safari cannot count frames
    // queued for decode but reports how long each spent inside, and by Little's law
    // `decodeFps × residency` is how many were in there. Omitted until two polls exist.
    const decodeMsPerFrame =
      previous !== null && decodedDelta > 0 && reading.totalDecodeTime !== undefined
        ? Math.max(0, ((reading.totalDecodeTime - previous.totalDecodeTime) * 1_000) / decodedDelta)
        : undefined;
    const avgInterFrameDelay =
      reading.totalInterFrameDelay !== undefined && (reading.framesDecoded ?? 0) > 0 ? (reading.totalInterFrameDelay * 1000) / (reading.framesDecoded ?? 1) : 0;

    // PlaycastWebrtcQualityRawStats types these as plain numbers (the wire schema is looser
    // and allows null). 0 is the "no framerate observed yet" value, which is what
    // recommendationFromStats reads to detect an insufficient sample window.
    const hasFramerateSamples = this.framerateWindow.length > 0;
    const avgFrameRate = average(this.framerateWindow);
    const maxRecentFrameRate = hasFramerateSamples ? Math.max(...this.framerateWindow) : 0;
    const minRecentFrameRate = hasFramerateSamples ? Math.min(...this.framerateWindow) : 0;
    const spread = hasFramerateSamples ? maxRecentFrameRate - minRecentFrameRate : 0;

    const raw: PlaycastWebrtcQualityRawStats = {
      hasActiveVideoTrack: true,
      currentRoundTripTime: reading.rttMs !== undefined ? reading.rttMs / 1000 : 0,
      averageDecodeTimePerFrame: averageDecodeTimePerFrameMs,
      jitter: reading.jitter,
      jitterBufferDelay: jitterBuffer.delayMs ?? 0,
      packetLossPercentage,
      packetSamples: [...this.packetSamples],
      recentFreezeDelta: freezeDelta,
      freezeBaseline: previous?.freezeCount ?? 0,
      recentFreezes: [...this.recentFreezes],
      avgInterFrameDelay,
      nackBaseline: previous?.nackCount ?? 0,
      recentNackDelta: nackDelta,
      recentNacks: [...this.recentNacks],
      bitsPerSecond,
      videoKbps,
      avgFrameRate,
      maxRecentFrameRate,
      minRecentFrameRate,
      isFrameRateAboveMinimum: avgFrameRate >= MIN_ACCEPTABLE_FPS,
      isFrameRateStable: spread <= FRAMERATE_STABILITY_SPREAD,
      isFrameRatePanic: hasFramerateSamples && avgFrameRate <= FRAMERATE_PANIC_FPS,
      isBitratePanic: previous !== null && videoKbps <= BITRATE_PANIC_KBPS,
      isMobile: this.isMobile,
    };

    const congestion: StreamCongestionInputs = {
      standingQueueMs,
      packetsLostPct: previous && intervalMs > 0 && reading.packetsLost !== undefined && reading.packetsReceived !== undefined ? packetLossPercentage : undefined,
      playoutLatencyTrendMs: playoutLatencyTrendMs ?? undefined,
      // Omitted when the engine cannot measure framesReceived (Safari): absence is "no
      // evidence", never a fabricated zero backlog.
      decodeBacklogFrames: previous && framesReceivedSignalAvailable ? decodeBacklogFrames : undefined,
      // Omitted rather than zeroed when the browser does not measure freezes: the ladder
      // treats an absent input as "no evidence" and a 0 as "evidence of health".
      freezeDelta: usableFreezeDelta,
    };

    // Omit-don't-zero throughout: an engine that did not report a field must not read at
    // the host as a measured 0 (e.g. a decoder collapsed to 0fps). A measured 0 is sent as 0.
    const wire = omitUndefined<ClientStreamStatsPayload>({
      rttMs: roundOptional(reading.rttMs),
      rttFloorMs: roundOptional(rttFloorMs),
      standingQueueMs: roundOptional(standingQueueMs),
      packetsLostPct: roundOptional(congestion.packetsLostPct, 2),
      jbDelayMs: roundOptional(jitterBuffer.delayMs),
      playoutLatencyTrendMs: roundOptional(playoutLatencyTrendMs),
      decodeBacklogFrames: congestion.decodeBacklogFrames,
      standingBacklogFrames,
      decodeFps: roundOptional(reading.framesPerSecond),
      videoBitrateBps: roundOptional(videoBitrateBps),
      receiveFramesDelta:
        previous?.framesReceivedAvailable &&
        framesReceivedSignalAvailable &&
        reading.framesReceived !== undefined
          ? receivedFramesDelta
          : undefined,
      decodeMsPerFrame: roundOptional(decodeMsPerFrame, 1),
      freezeDelta: congestion.freezeDelta,
      receiverCounters: usableFreezeDelta !== undefined || freezeMs !== undefined || (previous && reading.framesDropped !== undefined) ? {
        ...(usableFreezeDelta !== undefined ? { freezes: usableFreezeDelta } : {}),
        ...(freezeMs !== undefined ? { freezeMs } : {}),
        ...(previous && reading.framesDropped !== undefined ? { framesDropped: droppedFramesDelta } : {}),
      } : undefined,
      // Audio is observability-only: it feeds the unified health record, never a threshold.
      audioBitrateBps: roundOptional(audioBitrateBps),
      audioPacketsLostPct: roundOptional(audioPacketsLostPct, 2),
      audioLevel: roundOptional(reading.audioLevel, 3),
      // The jitter buffer as the engine reports it: the delay frames sat in it this
      // interval, plus the target and minimum the engine was holding it to.
      jitterBufferDelayMs: roundOptional(jitterBuffer.delayMs, 1),
      jitterBufferTargetDelayMs: roundOptional(jitterBuffer.targetMs, 1),
      jitterBufferMinimumDelayMs: roundOptional(jitterBuffer.minimumMs, 1),
      // The network's own jitter figure (RFC 3550, seconds on the stat) beside the buffer that
      // reacts to it, and the audio buffer that A/V sync could be holding the video one to.
      jitterMs: roundOptional(reading.jitterSeconds !== undefined ? reading.jitterSeconds * 1000 : undefined, 1),
      audioJitterBufferDelayMs: roundOptional(audioJitterBuffer.delayMs, 1),
      audioJitterBufferTargetDelayMs: roundOptional(audioJitterBuffer.targetMs, 1),
      // Which decoder the standing frames are queued in front of, and at what resolution.
      decoderImplementation: reading.decoderImplementation,
      powerEfficientDecoder: reading.powerEfficientDecoder,
      codecMimeType: reading.codecMimeType,
      frameWidth: reading.frameWidth,
      frameHeight: reading.frameHeight,
    });

    // DEBUG BUILD ONLY — one line per 1Hz poll. Live gate so a late `?debug` (or a test
    // flipping the flag) is honoured; a start-up snapshot would miss that.
    if (isDebugWebrtcLoggingEnabled()) {
      // The wire IS what the host sees; an omitted key here is an omitted key there.
      console.log('[GuestStats.tick]', { ...wire, framesDroppedTotal: reading.framesDropped ?? 'absent' });
    }

    return {
      selectedPath: reading.selectedPath,
      raw,
      congestion,
      wire,
      framesDecoded: reading.framesDecoded ?? 0,
      ...(reading.decodeSample ? { decodeSample: reading.decodeSample } : {}),
      ...(standingBacklogFrames !== undefined ? { standingBacklogFrames } : {}),
      freezeSignalAvailable,
    };
  }

  private pushWindow(fps: number): void {
    if (!Number.isFinite(fps) || fps <= 0) return;
    this.framerateWindow.push(fps);
    if (this.framerateWindow.length > FRAMERATE_WINDOW_SAMPLES) {
      this.framerateWindow.shift();
    }
  }

  /**
   * Interval averages of the three cumulative jitter-buffer sums (delay, target, minimum),
   * each divided by the frames emitted this interval. Trusted only when the engine exposes
   * the counters; null on the first poll, on a counter reset, and where a sum is absent.
   */
  private intervalJitterBuffer(reading: InboundRtpReading): JitterBufferInterval {
    const available = this.capability?.exposesJitterBufferDelay ?? (reading.jbDelayCumulative !== undefined);
    if (!available || reading.jbDelayCumulative === undefined || reading.jbEmittedCumulative === undefined) {
      return NO_JITTER_BUFFER;
    }
    const previous = this.prevJitterBuffer;
    this.prevJitterBuffer = {
      delay: reading.jbDelayCumulative,
      target: reading.jbTargetDelayCumulative ?? Number.NaN,
      minimum: reading.jbMinimumDelayCumulative ?? Number.NaN,
      emitted: reading.jbEmittedCumulative,
    };
    if (previous === null) return NO_JITTER_BUFFER;
    const emittedDelta = reading.jbEmittedCumulative - previous.emitted;
    if (emittedDelta <= 0) return NO_JITTER_BUFFER;
    const perEmitted = (sum: number, previousSum: number): number | null => {
      if (!Number.isFinite(sum) || !Number.isFinite(previousSum) || sum < previousSum) return null;
      return ((sum - previousSum) / emittedDelta) * 1000;
    };
    return {
      delayMs: perEmitted(reading.jbDelayCumulative, previous.delay),
      targetMs: perEmitted(reading.jbTargetDelayCumulative ?? Number.NaN, previous.target),
      minimumMs: perEmitted(reading.jbMinimumDelayCumulative ?? Number.NaN, previous.minimum),
    };
  }

  /** The audio (NetEq) buffer, same interval-average rule; null where the engine omits it. */
  private intervalAudioJitterBuffer(reading: InboundRtpReading): AudioJitterBufferInterval {
    if (reading.audioJbDelayCumulative === undefined || reading.audioJbEmittedCumulative === undefined) {
      return NO_AUDIO_JITTER_BUFFER;
    }
    const previous = this.prevAudioJitterBuffer;
    this.prevAudioJitterBuffer = {
      delay: reading.audioJbDelayCumulative,
      target: reading.audioJbTargetDelayCumulative ?? Number.NaN,
      emitted: reading.audioJbEmittedCumulative,
    };
    if (previous === null) return NO_AUDIO_JITTER_BUFFER;
    const emittedDelta = reading.audioJbEmittedCumulative - previous.emitted;
    if (emittedDelta <= 0) return NO_AUDIO_JITTER_BUFFER;
    const perEmitted = (sum: number, previousSum: number): number | null => {
      if (!Number.isFinite(sum) || !Number.isFinite(previousSum) || sum < previousSum) return null;
      return ((sum - previousSum) / emittedDelta) * 1000;
    };
    return {
      delayMs: perEmitted(reading.audioJbDelayCumulative, previous.delay),
      targetMs: perEmitted(reading.audioJbTargetDelayCumulative ?? Number.NaN, previous.target),
    };
  }
}

/**
 * Quality recommendation from one sample.
 *
 * Replaces the recommendation logic in the deprecated `libs/Peer` quality manager. The reason
 * strings are preserved verbatim from `libs/Peer/src/lib/stats.ts` so existing analytics
 * queries and dashboards keyed on them keep working across the migration.
 */
export function recommendationFromStats(raw: PlaycastWebrtcQualityRawStats): {
  recommendation: PeerQualityRecommendation;
  reason: string;
} {
  // maxRecentFrameRate is 0 until at least one framerate sample has been observed.
  if (!raw.hasActiveVideoTrack || raw.maxRecentFrameRate <= 0) {
    return { recommendation: 'maintain', reason: 'INSUFFICIENT_FRAMERATE_SAMPLES' };
  }
  if (raw.isFrameRatePanic) {
    return { recommendation: 'panic', reason: 'FRAMERATE_PANIC' };
  }
  if (raw.isBitratePanic) {
    return { recommendation: 'panic', reason: 'BITRATE_PANIC' };
  }
  if (raw.averageDecodeTimePerFrame > DECODER_OVERRUN_MS) {
    return { recommendation: 'decrease', reason: 'DECODER_OVERRUN' };
  }
  if (raw.packetLossPercentage >= LOSS_DECREASE_PCT) {
    return { recommendation: 'decrease', reason: 'PACKET_LOSS' };
  }
  if (raw.recentFreezeDelta > FREEZE_DECREASE_DELTA) {
    return { recommendation: 'decrease', reason: 'TOO_MANY_FREEZES' };
  }
  if (raw.avgInterFrameDelay > INTER_FRAME_DELAY_DECREASE_MS) {
    return { recommendation: 'decrease', reason: 'HIGH_INTER_FRAME_DELAY' };
  }
  if (raw.recentNackDelta > NACK_DECREASE_DELTA) {
    return { recommendation: 'decrease', reason: 'HIGH_NACK_COUNT' };
  }
  if (raw.isFrameRateAboveMinimum && raw.isFrameRateStable && raw.packetLossPercentage === 0) {
    return { recommendation: 'increase', reason: 'GOOD_PERFORMANCE' };
  }
  return { recommendation: 'maintain', reason: '' };
}

/** Counter deltas clamp at 0 so an SSRC reset re-baselines instead of fabricating a spike. */
function delta(current: number | undefined, previous: number | undefined): number {
  if (current === undefined || previous === undefined) return 0;
  return Math.max(0, current - previous);
}

function average(values: number[]): number {
  if (!values.length) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function round(value: number, decimals = 0): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

/** Round a reading that may be absent (undefined) or not yet measurable (null). */
function roundOptional(value: number | null | undefined, decimals = 0): number | undefined {
  return value === null || value === undefined ? undefined : round(value, decimals);
}

/** Drop undefined fields so absence is absence on the wire, not an explicit undefined. */
function omitUndefined<T extends object>(value: { [K in keyof T]: T[K] | undefined }): T {
  return Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as T;
}
